'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import StarRating from '@/components/ui/StarRating';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import BookingModal from './BookingModal';

const apartments = [
{
  id: 'apt-001',
  name: 'Skyline Penthouse Suite',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_191fc04c8-1772200208974.png",
  alt: 'Modern luxury penthouse with floor-to-ceiling windows overlooking city skyline at dusk',
  rating: 4.9,
  reviews: 84,
  status: 'available' as const,
  bedrooms: 3,
  bathrooms: 2,
  sqft: 1850,
  floor: '28th',
  amenities: ['Pool', 'Gym', 'Concierge', 'Parking'],
  rate: 4200,
  rateUnit: '/month',
  location: 'Downtown Financial District',
  furnished: true
},
{
  id: 'apt-002',
  name: 'Garden View Studio',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_114fe1256-1767382359440.png",
  alt: 'Bright modern studio apartment with large windows and minimalist furniture overlooking green garden',
  rating: 4.6,
  reviews: 52,
  status: 'available' as const,
  bedrooms: 1,
  bathrooms: 1,
  sqft: 620,
  floor: '4th',
  amenities: ['Garden Access', 'Laundry', 'Security'],
  rate: 1450,
  rateUnit: '/month',
  location: 'Midtown East',
  furnished: true
},
{
  id: 'apt-003',
  name: 'Heritage Brownstone 2BR',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13c32ebf0-1764686579749.png",
  alt: 'Classic brownstone apartment with hardwood floors and exposed brick wall warm lighting',
  rating: 4.7,
  reviews: 118,
  status: 'booked' as const,
  bedrooms: 2,
  bathrooms: 1,
  sqft: 980,
  floor: '2nd',
  amenities: ['Parking', 'Storage', 'Pet Friendly'],
  rate: 2800,
  rateUnit: '/month',
  location: 'Upper West Side',
  furnished: false
},
{
  id: 'apt-004',
  name: 'Waterfront Loft',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b32f3713-1773156343957.png",
  alt: 'Open-plan loft apartment with industrial design exposed beams and large windows facing waterfront',
  rating: 4.8,
  reviews: 67,
  status: 'available' as const,
  bedrooms: 2,
  bathrooms: 2,
  sqft: 1320,
  floor: '6th',
  amenities: ['Rooftop', 'Gym', 'Bike Storage', 'Doorman'],
  rate: 3600,
  rateUnit: '/month',
  location: 'Brooklyn Waterfront',
  furnished: true
},
{
  id: 'apt-005',
  name: 'Executive 1BR Residence',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_178e8644a-1773681264284.png",
  alt: 'Sleek modern one-bedroom apartment with white walls and premium finishes in executive building',
  rating: 4.5,
  reviews: 39,
  status: 'available' as const,
  bedrooms: 1,
  bathrooms: 1,
  sqft: 750,
  floor: '12th',
  amenities: ['Concierge', 'Gym', 'Business Center'],
  rate: 2100,
  rateUnit: '/month',
  location: 'Midtown West',
  furnished: true
},
{
  id: 'apt-006',
  name: 'Soho Artist Loft',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_192c50eac-1772732767250.png",
  alt: 'Spacious artist loft with high ceilings exposed brick and creative open floor plan in Soho',
  rating: 4.4,
  reviews: 28,
  status: 'available' as const,
  bedrooms: 2,
  bathrooms: 1,
  sqft: 1100,
  floor: '3rd',
  amenities: ['High Ceilings', 'Storage', 'Freight Elevator'],
  rate: 3200,
  rateUnit: '/month',
  location: 'SoHo',
  furnished: false
}];


export default function ApartmentCards() {
  const [bookingTarget, setBookingTarget] = useState<(typeof apartments)[0] | null>(null);
  const [sortBy, setSortBy] = useState('rating');
  const [furnishedOnly, setFurnishedOnly] = useState(false);

  const filtered = apartments.
  filter((a) => !furnishedOnly || a.furnished).
  sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'price-low') return a.rate - b.rate;
    if (sortBy === 'price-high') return b.rate - a.rate;
    if (sortBy === 'size') return b.sqft - a.sqft;
    return 0;
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{filtered.length}</span> apartments found
          </p>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={furnishedOnly}
              onChange={(e) => setFurnishedOnly(e.target.checked)}
              className="w-4 h-4 accent-primary rounded" />

            Furnished only
          </label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-border rounded-lg text-sm text-foreground bg-white outline-none cursor-pointer">

            <option value="rating">Top Rated</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="size">Largest First</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {filtered.map((apt) =>
        <div
          key={apt.id}
          className="bg-white rounded-xl border border-border shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden group">

            <div className="relative h-52 overflow-hidden">
              <AppImage
              src={apt.image}
              alt={apt.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw" />

              <div className="absolute top-3 left-3">
                <StatusBadge status={apt.status} size="sm" />
              </div>
              {apt.furnished &&
            <div className="absolute top-3 right-3 bg-accent text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  Furnished
                </div>
            }
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs flex items-center gap-1">
                  <Icon name="MapPinIcon" size={12} />
                  {apt.location}
                </p>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-display font-semibold text-base text-foreground leading-snug">{apt.name}</h3>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <StarRating value={apt.rating} size={13} />
                <span className="text-xs text-muted-foreground">{apt.rating} ({apt.reviews})</span>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center bg-muted rounded-lg py-1.5">
                  <p className="text-xs text-muted-foreground">Beds</p>
                  <p className="font-semibold text-sm text-foreground">{apt.bedrooms}</p>
                </div>
                <div className="text-center bg-muted rounded-lg py-1.5">
                  <p className="text-xs text-muted-foreground">Baths</p>
                  <p className="font-semibold text-sm text-foreground">{apt.bathrooms}</p>
                </div>
                <div className="text-center bg-muted rounded-lg py-1.5">
                  <p className="text-xs text-muted-foreground">Sq.ft</p>
                  <p className="font-semibold text-sm text-foreground tabular-nums">{apt.sqft}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                {apt.amenities.map((a) =>
              <span key={`${apt.id}-amenity-${a}`} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                    {a}
                  </span>
              )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-display font-bold text-xl text-primary tabular-nums">${apt.rate.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">{apt.rateUnit}</span>
                </div>
                <button
                onClick={() => setBookingTarget(apt)}
                disabled={apt.status === 'booked'}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95 ${
                apt.status === 'booked' ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-light'}`
                }>

                  {apt.status === 'booked' ? 'Unavailable' : 'Book Now'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {bookingTarget &&
      <BookingModal
        open={!!bookingTarget}
        onClose={() => setBookingTarget(null)}
        serviceType="apartment"
        serviceName={bookingTarget.name}
        serviceRate={bookingTarget.rate}
        serviceRateUnit={bookingTarget.rateUnit}
        serviceImage={bookingTarget.image}
        serviceImageAlt={bookingTarget.alt} />

      }
    </div>);

}